import {inject, Injectable, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {
  CustomerUserControllerService,
  CustomerUserResponse,
  ProviderUserControllerService,
  ProviderUserResponse,
} from '@frontend/shared-api';
import {defer, EMPTY, Observable, shareReplay} from 'rxjs';
import {AuthService} from './auth.service';
import {RoleCheckService} from './role-check.service';

export type UnifiedUser = CustomerUserResponse | ProviderUserResponse;

@Injectable({
  providedIn: 'root',
})
export class UserStateService {
  private readonly customerUserController = inject(CustomerUserControllerService);
  private readonly providerUserController = inject(ProviderUserControllerService);
  private readonly authService = inject(AuthService);
  private readonly roleCheckService = inject(RoleCheckService);

  private readonly currentUser$: Observable<UnifiedUser> = defer(() => {
    if (!this.authService.isAuthenticated()) {
      return EMPTY;
    }

    if (this.roleCheckService.hasSelecthorAdminRole()) {
      return EMPTY;
    }

    return this.hasCustomerRole()
      ? this.customerUserController.getCurrentCustomerUser()
      : this.providerUserController.getCurrentProviderUser();
  }).pipe(shareReplay(1));

  readonly currentUser = toSignal(this.currentUser$, {
    initialValue: null,
  });

  private readonly _isLoading = signal<boolean>(false);
  readonly isLoading = this._isLoading.asReadonly();

  getCurrentUser$(): Observable<UnifiedUser> {
    return this.currentUser$;
  }

  getCurrentUserLanguageCode(): string | null {
    const user = this.currentUser();
    return user?.languageCode?.trim() || null;
  }

  getCurrentUserKeycloakId(): string | null {
    const user = this.currentUser();
    return user?.keycloakId || null;
  }

  isCustomerUser(): boolean {
    return this.hasCustomerRole();
  }

  isProviderUser(): boolean {
    return this.hasProviderRole();
  }

  isCustomerUserType(user: UnifiedUser | null): user is CustomerUserResponse {
    return user != null && 'phoneNumber' in user;
  }

  private hasCustomerRole(): boolean {
    return (
      this.roleCheckService.hasCustomerUserRole() || this.roleCheckService.hasCustomerAdminRole()
    );
  }

  private hasProviderRole(): boolean {
    return (
      this.roleCheckService.hasProviderAdminRole() || this.roleCheckService.hasProviderUserRole()
    );
  }
}
